import CryptoJS from 'crypto-js';

import Configs from '@/Configs';
import { canonicalizeCompanyId } from '@/common/canonicalizeCompanyId';
import type { CompanyPreferences } from '@/common/dataContext';
import { queriesKeys } from '@/queries/queries-keys';
import { platformQueryClient } from '@/queries/query-client';

/** @deprecated Use {@link canonicalizeCompanyId}. */
export const canonicalizeCompanyPreferencesCompanyId = canonicalizeCompanyId;

/**
 * Backend `GET /companyPreferences/` returns an array of preference docs.
 * Login / profile / sessionStorage use a single document. Normalize both.
 *
 * @param {unknown} result
 * @returns {CompanyPreferences}
 */
export function normalizeCompanyPreferencesResponse(result: unknown): CompanyPreferences {
  if (result === 204 || result == null) {
    throw new Error('companyPreferences: empty');
  }

  const candidate = Array.isArray(result) ? result[0] : result;
  if (!candidate || typeof candidate !== 'object') {
    throw new Error('companyPreferences: unexpected response shape');
  }

  return candidate as CompanyPreferences;
}

/**
 * Read decrypted company preferences from sessionStorage (same shape as legacy DataProvider init).
 *
 * @returns {CompanyPreferences | null}
 */
export function readCompanyPreferencesFromSession(): CompanyPreferences | null {
  try {
    const storedData = sessionStorage.getItem('companyPreferences');
    if (!storedData) return null;
    const decrypted = CryptoJS.AES.decrypt(storedData, Configs.privateKey).toString(
      CryptoJS.enc.Utf8
    );
    if (!decrypted) return null;
    return JSON.parse(decrypted) as CompanyPreferences;
  } catch (error) {
    console.error('[companyPreferencesCache] Failed to parse companyPreferences from sessionStorage', error);
    return null;
  }
}

/**
 * Persist preferences to TanStack cache + encrypted sessionStorage (legacy readers).
 * Pass `null` to clear both.
 *
 * @param {string | number | null | undefined} companyID
 * @param {CompanyPreferences | null} value
 */
export function writeCompanyPreferencesCache(
  companyID: string | number | null | undefined,
  value: CompanyPreferences | null
): void {
  const canonicalCompanyID = canonicalizeCompanyId(companyID);
  if (canonicalCompanyID != null) {
    const key = queriesKeys.getCompanyPreferences(canonicalCompanyID);
    if (value == null) {
      platformQueryClient.removeQueries({ queryKey: key });
    } else {
      platformQueryClient.setQueryData(key, value);
    }
  }

  try {
    if (value) {
      sessionStorage.setItem(
        'companyPreferences',
        CryptoJS.AES.encrypt(JSON.stringify(value), Configs.privateKey).toString()
      );
    } else {
      sessionStorage.removeItem('companyPreferences');
    }
  } catch (error) {
    console.error('[companyPreferencesCache] Failed to write companyPreferences sessionStorage', error);
  }
}
